import {useContext} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {Checkbox, CHECKBOX_VARIANTS} from '@adv-mt/ui/checkbox'
import {Field} from '@adv-mt/ui/field'
import {Body2} from '@adv-mt/ui/typography'

import {useFormContext} from '../form/formContexts.js'
import {V2_FIELDS} from '../schema/contactSchema.js'
import {BASE_CLASS, FieldError} from './FormFields.js'

const TERMS_ID = 'contact-form-v2-terms'
const TERMS_ERROR_ID = `${TERMS_ID}-error`
const TERMS_ERROR_LITERAL = 'CONTACT_SELLER_V2.FORM.TERMS_ERROR'

const SENTINEL = {
  CONDITIONS_OPEN: '[[c]]',
  CONDITIONS_CLOSE: '[[/c]]',
  PRIVACY_OPEN: '[[p]]',
  PRIVACY_CLOSE: '[[/p]]'
}

const SPLIT_PATTERN = /(\[\[\/?[cp]\]\])/

const buildLegalLabel = ({text, conditionsHref, privacyHref}) => {
  const nodes = []
  let href = null
  let buffer = ''

  const flush = key => {
    if (!buffer) {
      return
    }

    nodes.push(
      href ? (
        <a key={key} className={`${BASE_CLASS}-link`} href={href} target="_blank" rel="noopener noreferrer">
          {buffer}
        </a>
      ) : (
        buffer
      )
    )
    buffer = ''
  }

  text.split(SPLIT_PATTERN).forEach((part, index) => {
    switch (part) {
      case SENTINEL.CONDITIONS_OPEN:
        flush(index)
        href = conditionsHref
        break
      case SENTINEL.PRIVACY_OPEN:
        flush(index)
        href = privacyHref
        break
      case SENTINEL.CONDITIONS_CLOSE:
      case SENTINEL.PRIVACY_CLOSE:
        flush(index)
        href = null
        break
      default:
        buffer += part
    }
  })

  flush('tail')

  return nodes
}

const TermsField = ({hasAlert = false}) => {
  const {i18n, domain} = useContext(Context)
  const form = useFormContext()
  const {DATA_PROTECTION_PATH_PATTERN} = domain.get('config').get('page')

  const label = buildLegalLabel({
    text: i18n.t('FORM.LEGAL_CHECKBOX_LABEL', {
      linkStartConditions: SENTINEL.CONDITIONS_OPEN,
      linkEndConditions: SENTINEL.CONDITIONS_CLOSE,
      linkStartPrivacy: SENTINEL.PRIVACY_OPEN,
      linkEndPrivacy: SENTINEL.PRIVACY_CLOSE
    }),
    conditionsHref: i18n.t('URL_COCHES_CONDITIONS'),
    privacyHref: DATA_PROTECTION_PATH_PATTERN
  })

  return (
    <form.Field name={V2_FIELDS.TERMS}>
      {field => (
        <Field.Root className={`${BASE_CLASS}-terms`}>
          <Checkbox
            id={TERMS_ID}
            name={field.name}
            checked={field.state.value}
            variant={hasAlert ? CHECKBOX_VARIANTS.ERROR : CHECKBOX_VARIANTS.PRIMARY}
            aria-describedby={hasAlert ? TERMS_ERROR_ID : undefined}
            aria-invalid={hasAlert ? true : undefined}
            onCheckedChange={checked => {
              field.handleChange(checked)
              field.handleBlur()
            }}
          />
          <Field.Label htmlFor={TERMS_ID}>
            <Body2 as="span">{label}</Body2>
          </Field.Label>
          <FieldError id={TERMS_ERROR_ID} errorKey={hasAlert ? TERMS_ERROR_LITERAL : undefined} />
        </Field.Root>
      )}
    </form.Field>
  )
}

TermsField.displayName = 'ContactFormV2TermsField'

TermsField.propTypes = {
  hasAlert: PropTypes.bool
}

export default TermsField
